import React, { JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import { QueryInfo, QueryField, socialUniverseField, QueryFieldTypes } from "system/query/QueryInfo";
import TabbedDialog from "components/dialogs/TabbedDialog";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsSwitch from "components/settingsConfig/uiComponents/SettingsSwitch";
import { WidgetQueryBehavior, WidgetQueryRequirements, WidgetRequest, WidgetTemplateQueryBehavior } from "system/widget/Widget";

const defaultWidgetRequest = {
  name: 'subqueries',
  endpoint: 'query/agg',
  queryParams: {
    include_total: true
  },
  widgetQueryBehavior: WidgetQueryBehavior.MULTI_REQUEST,
  widgetTemplateQueryBehavior: WidgetTemplateQueryBehavior.SINGLE_COMBINED,
  widgetQueryRequirements: WidgetQueryRequirements.REQUIRES_WIDGET_AND_TEMPLATE_QUERIES
};

const defaultRootWidgetRequest = {
  name: 'subqueries-root',
  endpoint: 'query/agg',
  queryParams: {
    include_total: true
  },
  widgetQueryBehavior: WidgetQueryBehavior.NONE,
  widgetTemplateQueryBehavior: WidgetTemplateQueryBehavior.SINGLE_COMBINED,
  widgetQueryRequirements: WidgetQueryRequirements.REQUIRES_WIDGET_AND_TEMPLATE_QUERIES
};

export function BuildDefaultSubqueriesRequests(queryInfo: QueryInfo, settingsState?: SubqueriesSettingsStateType): WidgetRequest[] | undefined {
  const foundTimestampField = queryInfo.fields?.find((field:QueryField) => field.type === QueryFieldTypes.timestamp) || false;

  let aggs : Record<string, any> = {};

  if (foundTimestampField) {
    const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;
    aggs = {
      histogram: {
        op: "histogram",
        field: foundTimestampField.id,
        ...(foundSocialUniverseField && {
          aggs: {
            social_universe: {
              op: socialUniverseField.id
            }
          }
        }),
      },
    };

    if (!settingsState?.sentimentColumnHidden) {
      aggs["sentiment"] = {
        "op": "keyword",
        "field": "sentiment",
      };
    }
    if (!settingsState?.keywordColumnHidden) {
      aggs["keyword"] = {
        "op": "keyword",
        "field": settingsState?.keywordType || "emotions",
      };
    }
  }

  const requestInit = { body: { aggs: aggs }};
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest, requestInit: { ...requestInit } };
  const rootWidgetRequest : WidgetRequest = { ...defaultRootWidgetRequest, requestInit: { ...requestInit } };

  return [widgetRequest, rootWidgetRequest];
}

export type SubqueriesSettingsStateType = {
  customWidgetTitle: string;
  widgetTitle: string;
  widgetTitleAlignment: string;
  volumeField: "records" | "social_universe";
  volumeColumnHidden: boolean;
  sparklineColumnHidden: boolean;
  keywordType: "emotions" | "themes";
  sentimentColumnHidden: boolean;
  keywordColumnHidden: boolean;
  sorting: "volume" | "sentiment" | "queryOrder";
}

export const defaultSettings: SubqueriesSettingsStateType = {
  customWidgetTitle: '',
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
  volumeField: 'social_universe',
  keywordType: 'emotions',
  sorting: 'volume',
  volumeColumnHidden: false,
  sparklineColumnHidden: false,
  sentimentColumnHidden: false,
  keywordColumnHidden: false,
}

export default function SubqueriesSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const updatedDefaultSettings = {...defaultSettings, color: theme.colors.primary.dark, ...props?.widget?.data?.settings};

  const [settingsState, setSettingsState] = useState<SubqueriesSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: SubqueriesSettingsStateType) => setSettingsState(updatedState);

  // check if the data object has the fields needed
  const updateAggs = () => {
    if (props.queryInfo) {
      const requests = BuildDefaultSubqueriesRequests(props.queryInfo, settingsState);
      props.saveWidgetDataAndRequests({settings: settingsState}, requests).then(() => {
        if (props.widgetRefresh) {
          props.widgetRefresh();
        }
      });
    }
  };

  const volumeMetricOptions: Array<OptionsValueType> = [
    { value: "records", label: i18n.t("records") },
    { value: "social_universe", label: i18n.t("social_universe") }
  ];

  const keywordTypeOptions: Array<OptionsValueType> = [
    { value: "emotions", label: i18n.t("widget-types.post-list.post.emotions") },
    { value: "themes", label: i18n.t("widget-types.post-list.post.themes") }
  ];

  const sortingOptions: Array<OptionsValueType> = [
    { value: "volume", label: i18n.t("widget-types.subqueries.settings.volume") },
    { value: "sentiment", label: i18n.t("widget-types.post-list.post.sentiment") },
    { value: "queryOrder", label: i18n.t("widget-types.subqueries.settings.original-query-order") }
  ];

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        props.setSettingsDialogIsOpen(false);
      }}
      maxWidth="sm"
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data")]}
      title={i18n.t("widget-types.subqueries.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <SettingsOptions
              onChange={settingsOnChange}
              settingsState={settingsState}
              useFullWidth={true}
              sx={{ mt: 2 }}>
              {!(settingsState.volumeColumnHidden && settingsState.sparklineColumnHidden) && (
                <SettingsSingleSelect
                  defaultValue={volumeMetricOptions[0]}
                  keyid="volumeField"
                  label={i18n.t("widget-types.subqueries.settings.volume-metric")}
                  options={volumeMetricOptions}
                  value={volumeMetricOptions.find((item: OptionsValueType) => (
                    item.value === settingsState.volumeField
                  ))}
              />)}
              {!settingsState.keywordColumnHidden && (
                <SettingsSingleSelect
                  defaultValue={keywordTypeOptions[0]}
                  keyid="keywordType"
                  label={i18n.t("widget-types.subqueries.settings.keyword-metric")}
                  options={keywordTypeOptions}
                  value={keywordTypeOptions.find((item: OptionsValueType) => (
                    item.value === settingsState.keywordType
                  ))}
              />)}
              <SettingsSingleSelect
                defaultValue={sortingOptions[0]}
                keyid="sorting"
                label={i18n.t("widgets.settings.sorting.label")}
                options={sortingOptions}
                value={sortingOptions.find((item: OptionsValueType) => (
                  item.value === settingsState.sorting
                ))}
              />
              <Box sx={{ display: "grid", columnGap: 4, rowGap: 1, gridTemplateColumns: "1fr 1fr", mt: 2 }}>
                <SettingsSwitch
                  keyid="volumeColumnHidden"
                  label={i18n.t("widget-types.subqueries.settings.volume-column-hidden")}
                  value={settingsState.volumeColumnHidden}
                />
                <SettingsSwitch
                  keyid="sparklineColumnHidden"
                  label={i18n.t("widget-types.subqueries.settings.sparkline-column-hidden")}
                  value={settingsState.sparklineColumnHidden}
                />
                <SettingsSwitch
                  keyid="sentimentColumnHidden"
                  label={i18n.t("widget-types.subqueries.settings.sentiment-column-hidden")}
                  value={settingsState.sentimentColumnHidden}
                />
                <SettingsSwitch
                  keyid="keywordColumnHidden"
                  label={i18n.t("widget-types.subqueries.settings.keyword-column-hidden")}
                  value={settingsState.keywordColumnHidden}
                />
              </Box>
            </SettingsOptions>
          </>)}
        </>)}
      </>
    </TabbedDialog>
  )
}
